import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowCounterClockwise } from "@phosphor-icons/react";

export default function EditAIOutputDialog({ open, onOpenChange, caseId, aiOutput, onSaved }) {
    const [content, setContent] = useState("");
    const [saving, setSaving] = useState(false);
    const [reverting, setReverting] = useState(false);

    useEffect(() => {
        if (open && aiOutput) setContent(aiOutput.content || "");
    }, [open, aiOutput]);

    if (!aiOutput) return null;
    const hasBeenEdited = !!aiOutput.edited_at;
    const isDirty = content !== (aiOutput.content || "");
    const charCount = content.length;

    const save = async () => {
        if (!isDirty) { onOpenChange(false); return; }
        setSaving(true);
        try {
            await api.patch(`/cases/${caseId}/ai-outputs/${aiOutput.id}`, { content });
            toast.success("Letter updated");
            onOpenChange(false);
            if (onSaved) onSaved();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const revert = async () => {
        if (!hasBeenEdited) return;
        if (!window.confirm("Restore the AI's original letter? Your edits will be lost.")) return;
        setReverting(true);
        try {
            await api.post(`/cases/${caseId}/ai-outputs/${aiOutput.id}/revert`);
            toast.success("Reverted to AI original");
            onOpenChange(false);
            if (onSaved) onSaved();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setReverting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-3xl max-h-[90vh] flex flex-col" data-testid="edit-ai-dialog">
                <DialogHeader>
                    <DialogTitle>Edit letter</DialogTitle>
                    <DialogDescription>
                        Adjust the wording before you print, download, or mail. Addresses and the letterhead are already baked in — you can tweak them here too.
                    </DialogDescription>
                </DialogHeader>
                <Textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    className="flex-1 min-h-[400px] font-mono text-sm leading-relaxed"
                    data-testid="edit-ai-textarea"
                    autoFocus
                />
                <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>{charCount.toLocaleString()} characters</span>
                    <span>
                        {hasBeenEdited && (
                            <span className="text-amber-700 mr-3">
                                Edited {new Date(aiOutput.edited_at).toLocaleString()}
                            </span>
                        )}
                        {isDirty && <span className="text-slate-700">• unsaved changes</span>}
                    </span>
                </div>
                <DialogFooter className="gap-2 sm:justify-between">
                    <Button
                        variant="ghost"
                        onClick={revert}
                        disabled={!hasBeenEdited || reverting || saving}
                        data-testid="edit-ai-revert"
                        className="text-slate-600"
                    >
                        <ArrowCounterClockwise size={14} className="mr-1.5" />
                        {reverting ? "Reverting…" : "Restore AI original"}
                    </Button>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving || reverting}>Cancel</Button>
                        <Button onClick={save} disabled={saving || reverting || !isDirty} data-testid="edit-ai-save">
                            {saving ? "Saving…" : "Save changes"}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
